import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl, errorMessage } from "../lib/api";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Upload, BookOpen, Image as ImageIcon, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const KB_TYPES = [
    { value: "showroom", label: "Showroom" },
    { value: "certificate", label: "Sertifikat" },
    { value: "policy", label: "Kebijakan / Garansi" },
    { value: "tutorial", label: "Tutorial / Cara Pakai" },
    { value: "general", label: "Umum" },
];

export default function AdminKnowledge() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ title: "", type: "showroom", caption: "", tags: "" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/knowledge");
            setEntries(data || []);
        } catch (e) { toast.error(errorMessage(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Judul tidak boleh kosong");
            return;
        }
        setCreating(true);
        try {
            await api.post("/admin/knowledge", {
                title: form.title.trim(),
                type: form.type,
                caption: form.caption.trim(),
                tags: form.tags.split(/[,;]/).map((t) => t.trim()).filter(Boolean),
            });
            setForm({ title: "", type: "showroom", caption: "", tags: "" });
            toast.success("Entry dibuat");
            await load();
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Hapus entry ini?")) return;
        try {
            await api.delete(`/admin/knowledge/${id}`);
            toast.success("Entry dihapus");
            await load();
        } catch (err) { toast.error(errorMessage(err)); }
    };

    return (
        <div className="min-h-screen bg-secondary/30 pb-20">
            <header className="border-b border-border bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
                    <Link to="/admin" data-testid="back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <ArrowLeft size={16} /> Kembali ke Dashboard
                    </Link>
                    <h1 className="text-base font-semibold inline-flex items-center gap-2"><BookOpen size={16} /> Knowledge Base</h1>
                    <div />
                </div>
            </header>

            <div className="mx-auto max-w-7xl space-y-6 px-5 py-6">
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
                    <p className="text-sm">
                        🤖 <strong>Knowledge Base</strong> digunakan oleh AI bot WhatsApp untuk menjawab pertanyaan
                        umum tentang toko (lokasi, garansi, sertifikat, dll) dengan kirim foto otomatis.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                        Tambahkan entry dengan <strong>tags</strong> (kata kunci) yang sering ditanya customer.
                        Saat customer kirim pesan yang mengandung tag, bot otomatis kirim foto + caption entry ini.
                    </p>
                </div>

                {/* Create form */}
                <form onSubmit={handleCreate} className="rounded-xl border border-border bg-white p-5 sm:p-6 space-y-4">
                    <h3 className="text-base font-semibold">Tambah Entry Baru</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="label-eyebrow text-muted-foreground">Judul</span>
                            <input data-testid="kb-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" placeholder="Showroom Reno Point" />
                        </label>
                        <label className="block">
                            <span className="label-eyebrow text-muted-foreground">Tipe</span>
                            <select data-testid="kb-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm">
                                {KB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Caption (akan tampil di pesan WA)</span>
                        <textarea data-testid="kb-caption-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={2} className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" placeholder="Alamat: Jl. ..., buka Senin-Sabtu 09.00-17.00" />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Tags (pisahkan dengan koma)</span>
                        <input data-testid="kb-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm" placeholder="alamat, showroom, toko, dimana, lokasi" />
                        <span className="block mt-1 text-[11px] text-muted-foreground">Customer kirim pesan dengan kata kunci ini → bot otomatis kirim entry ini.</span>
                    </label>
                    <button type="submit" disabled={creating} data-testid="kb-create-btn" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50">
                        {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Tambah Entry
                    </button>
                </form>

                {/* Entry list */}
                <div className="space-y-3">
                    {loading && <div className="text-sm text-muted-foreground">Loading...</div>}
                    {!loading && entries.length === 0 && (
                        <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
                            Belum ada entry. Buat entry pertama di atas.
                        </div>
                    )}
                    {entries.map((e) => <EntryCard key={e.id} entry={e} onDelete={() => handleDelete(e.id)} onRefresh={load} />)}
                </div>
            </div>
        </div>
    );
}

function EntryCard({ entry, onDelete, onRefresh }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        title: entry.title || "",
        type: entry.type || "general",
        caption: entry.caption || "",
        tags: (entry.tags || []).join(", "),
    });

    const startEdit = () => {
        setEditForm({
            title: entry.title || "",
            type: entry.type || "general",
            caption: entry.caption || "",
            tags: (entry.tags || []).join(", "),
        });
        setEditing(true);
    };

    const cancelEdit = () => setEditing(false);

    const handleSave = async () => {
        if (!editForm.title.trim()) {
            toast.error("Judul tidak boleh kosong");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/admin/knowledge/${entry.id}`, {
                title: editForm.title.trim(),
                type: editForm.type,
                caption: editForm.caption.trim(),
                tags: editForm.tags.split(/[,;]/).map((t) => t.trim()).filter(Boolean),
            });
            toast.success("Entry diperbarui");
            setEditing(false);
            await onRefresh();
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/admin/knowledge/${entry.id}/image`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Foto diunggah");
            await onRefresh();
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid={`kb-entry-${entry.id}`} className="rounded-xl border border-border bg-white p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-40 shrink-0">
                    {entry.image_path ? (
                        <img src={imageUrl(entry.image_path)} alt={entry.title} className="w-full aspect-square object-cover rounded-lg border border-border" />
                    ) : (
                        <div className="w-full aspect-square rounded-lg border border-dashed border-border bg-secondary/30 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                            <ImageIcon size={28} strokeWidth={1.25} />
                            <span className="text-[11px]">Belum ada foto</span>
                        </div>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid={`kb-image-input-${entry.id}`} />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`kb-upload-btn-${entry.id}`} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-white px-2 py-1.5 text-xs hover:bg-secondary disabled:opacity-50">
                        {uploading ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                        {entry.image_path ? "Ganti Foto" : "Upload Foto"}
                    </button>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                    {editing ? (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="label-eyebrow text-muted-foreground">Judul</span>
                                    <input
                                        data-testid={`kb-edit-title-${entry.id}`}
                                        value={editForm.title}
                                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                        className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                                    />
                                </label>
                                <label className="block">
                                    <span className="label-eyebrow text-muted-foreground">Tipe</span>
                                    <select
                                        data-testid={`kb-edit-type-${entry.id}`}
                                        value={editForm.type}
                                        onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                                        className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                                    >
                                        {KB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </label>
                            </div>
                            <label className="block">
                                <span className="label-eyebrow text-muted-foreground">Caption</span>
                                <textarea
                                    data-testid={`kb-edit-caption-${entry.id}`}
                                    value={editForm.caption}
                                    onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                                    rows={2}
                                    className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="block">
                                <span className="label-eyebrow text-muted-foreground">Tags (pisahkan dengan koma)</span>
                                <input
                                    data-testid={`kb-edit-tags-${entry.id}`}
                                    value={editForm.tags}
                                    onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                                    className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                                />
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    data-testid={`kb-save-btn-${entry.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:opacity-90 disabled:opacity-50"
                                >
                                    {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Simpan
                                </button>
                                <button
                                    onClick={cancelEdit}
                                    disabled={saving}
                                    data-testid={`kb-cancel-btn-${entry.id}`}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs hover:bg-secondary disabled:opacity-50"
                                >
                                    <X size={12} /> Batal
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h4 className="font-semibold">{entry.title}</h4>
                                    <span className="label-eyebrow text-primary text-[10px]">{KB_TYPES.find((t) => t.value === entry.type)?.label || entry.type}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={startEdit}
                                        data-testid={`kb-edit-btn-${entry.id}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                                        title="Edit entry"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        onClick={onDelete}
                                        data-testid={`kb-delete-btn-${entry.id}`}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-destructive hover:bg-destructive/10"
                                        title="Hapus entry"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            {entry.caption && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{entry.caption}</p>}
                            {entry.tags && entry.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {entry.tags.map((t) => (
                                        <span key={t} className="inline-block rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground">{t}</span>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
